import { useLanguage } from '../LanguageContext';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { MessageSquare, Send, ChevronLeft, User, Clock } from 'lucide-react';

const EMOJIS = ['😊','👍','✅','📦','🚛','⏰','❌','🙏','📞','💬','📄','🔔'];

const CustomerMessages = ({
  embedded = false }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [fotoData, setFotoData] = useState(null);
  const [aanvragen, setAanvragen] = useState([]);
  const [selectedAanvraag, setSelectedAanvraag] = useState('');
  const [unreadAanvragen, setUnreadAanvragen] = useState(new Set());
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const loadMessagesRef = useRef(null); // Ref voor altijd de laatste loadMessages

  const loadMessages = useCallback(async (isInit = false) => {
    try {
      const response = await fetch('/api/customer/messages', { credentials: 'include' });
      const data = await response.json();
      if (data.success) {
        const newMsgs = data.messages || [];
        setMessages(prev => {
          const prevIds = prev.map(m => m.id).join(',');
          const newIds = newMsgs.map(m => m.id).join(',');
          return prevIds === newIds ? prev : newMsgs;
        });
        const unreadIds = (data.messages || [])
          .filter(m => m.sender_type === 'admin' && !m.is_read)
          .map(m => m.id);
        // Bijhouden welke aanvragen ongelezen berichten hebben
        const unreadAanvraagIds = new Set(
          (data.messages || [])
            .filter(m => m.sender_type === 'admin' && !m.is_read && m.aanvraag_id)
            .map(m => m.aanvraag_id)
        );
        setUnreadAanvragen(unreadAanvraagIds);
        if (unreadIds.length > 0) markAsRead(unreadIds);
      }
    } catch (err) {
      console.error('Load messages error:', err);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Houd ref altijd up-to-date
  useEffect(() => {
    loadMessagesRef.current = loadMessages;
  }, [loadMessages]);

  useEffect(() => {
    checkAuth();
    loadMessages(true);
    loadAanvragen();

    // Polling als fallback (5 seconden)
    const interval = setInterval(() => loadMessagesRef.current(false), 5000);

    // Socket.io voor real-time berichten
    const socket = io({ path: '/socket.io', transports: ['websocket', 'polling'] });
    socket.on('nieuw-admin-bericht', () => loadMessagesRef.current(false));
    socket.on('nieuw-klant-bericht', () => loadMessagesRef.current(false));

    return () => {
      clearInterval(interval);
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadAanvragen = async () => {
    try {
      const res = await fetch('/api/customer/aanvragen', { credentials: 'include' });
      const data = await res.json();
      if (data.success) setAanvragen(data.aanvragen || []);
    } catch(e) {}
  };

  const checkAuth = async () => {
    try {
      const response = await fetch('/api/customer/check-auth', { credentials: 'include' });
      const data = await response.json();
      if (!data.authenticated) navigate('/customer/login');
    } catch (err) {
      navigate('/customer/login');
    }
  };

  const markAsRead = async (ids) => {
    try {
      await fetch('/api/customer/messages/mark-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ messageIds: ids })
      });
    } catch(e) {}
  };

  const handleFotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setFotoPreview(ev.target.result);
      setFotoData(ev.target.result);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSend = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if ((!newMessage.trim() && !fotoData) || sending) return;
    setSending(true);
    try {
      const berichtTekst = fotoData
        ? (newMessage.trim() ? newMessage.trim() + '\n[foto]' : '[foto]')
        : newMessage.trim();
      const response = await fetch('/api/customer/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: berichtTekst,
          imageData: fotoData || null,
          aanvraagId: selectedAanvraag || null,
          onderwerp: selectedAanvraag
            ? (aanvragen.find(a => a.AanvraagID === selectedAanvraag)
                ? `${selectedAanvraag} — ${aanvragen.find(a => a.AanvraagID === selectedAanvraag).LaadPlaats} → ${aanvragen.find(a => a.AanvraagID === selectedAanvraag).LosPlaats}`
                : selectedAanvraag)
            : t('algemeen')
        })
      });
      const data = await response.json();
      if (data.success) {
        setNewMessage('');
        setFotoPreview(null);
        setFotoData(null);
        loadMessages();
        inputRef.current?.focus();
      }
    } catch (err) {
      console.error('Send message error:', err);
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const formatTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (date.toDateString() === today.toDateString())
        return date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
      if (date.toDateString() === yesterday.toDateString())
        return t('gisteren') + ' ' + date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
      return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) + ' ' +
        date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
    } catch { return timestamp; }
  };

  if (loading) return (
    <div className={embedded ? "flex items-center justify-center p-8" : "min-h-screen flex items-center justify-center bg-gray-50"}>
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
    </div>
  );

  return (
    <div className={embedded ? "flex flex-col" : "min-h-screen bg-gray-50 flex flex-col"} style={embedded ? { height: '75vh' } : {}}>
      {!embedded && (
      <div className="bg-white shadow-md flex-shrink-0">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/customer/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <ChevronLeft className="w-6 h-6 text-gray-700" />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-800">{t('berichten_titel')}</h1>
              <p className="text-sm text-gray-600">{t('chat_team')}</p>
            </div>
            <div className="flex items-center gap-2 text-green-600">
              <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">{t('online')}</span>
            </div>
          </div>
        </div>
      </div>
      )}

      <div className="flex-1 overflow-hidden">
        <div className="max-w-4xl mx-auto h-full flex flex-col">
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
            {(() => {
              const filtered = selectedAanvraag
                ? messages.filter(m => m.aanvraag_id === selectedAanvraag)
                : messages;
              if (filtered.length === 0) return (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <MessageSquare className="w-16 h-16 text-gray-400 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {selectedAanvraag ? t('geen_berichten_aanvraag') : t('geen_berichten_nog')}
                  </h3>
                  <p className="text-gray-600">
                    {selectedAanvraag ? t('filter_tip') : t('start_gesprek')}
                  </p>
                </div>
              );
              return filtered.map((message, index) => (
                <div key={index} className={`flex ${message.sender_type === 'customer' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`flex gap-3 max-w-[80%] ${message.sender_type === 'customer' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.sender_type === 'customer' ? 'bg-cyan-50' : 'bg-blue-100'}`}>
                      <User className={`w-5 h-5 ${message.sender_type === 'customer' ? 'text-blue-900' : 'text-blue-600'}`} />
                    </div>
                    <div>
                      <div className={`px-4 py-3 ${message.sender_type === 'customer'
                        ? 'bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-tr-none rounded-none'
                        : 'bg-white shadow-md text-gray-800 rounded-tl-none rounded-none'}`}>
                        {message.image_data && (
                          <img src={message.image_data} alt="afbeelding"
                            className="max-w-xs rounded-lg mb-2 block"
                            style={{ maxHeight: '200px', objectFit: 'contain' }} />
                        )}
                        {message.aanvraag_id && (
                          <div style={{ marginBottom: 6 }}>
                            <span style={{
                              display: 'inline-block',
                              background: message.sender_type === 'customer' ? 'rgba(255,255,255,0.2)' : '#dbeafe',
                              color: message.sender_type === 'customer' ? '#fff' : '#1e40af',
                              borderRadius: 20,
                              padding: '2px 10px',
                              fontSize: 11,
                              fontWeight: 700,
                              letterSpacing: '.03em'
                            }}>
                              📦 {message.aanvraag_id}
                              {message.onderwerp && message.onderwerp !== message.aanvraag_id &&
                                <span style={{ fontWeight: 400, opacity: 0.8 }}> — {message.onderwerp.replace(message.aanvraag_id + ' — ', '')}</span>
                              }
                            </span>
                          </div>
                        )}
                        {(() => {
                          const tekst = (message.message || '').replace('\n[foto]', '').replace('[foto]', '').trim();
                          return tekst ? <p className="text-sm whitespace-pre-wrap break-words">{tekst}</p> : null;
                        })()}
                      </div>
                      <div className={`flex items-center gap-1 mt-1 px-2 ${message.sender_type === 'customer' ? 'justify-end' : ''}`}>
                        <Clock className="w-3 h-3 text-gray-400" />
                        <span className="text-xs text-gray-500">{formatTime(message.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ));
            })()}
            <div ref={messagesEndRef} />
          </div>

          <div className="flex-shrink-0 bg-white border-t border-gray-200">
            <div className="px-4 sm:px-6 lg:px-8 pt-3 pb-1 flex items-center gap-3">
              <label className="text-sm font-medium text-gray-600 whitespace-nowrap">{t('betreft')}</label>
              <select
                value={selectedAanvraag}
                onChange={e => setSelectedAanvraag(e.target.value)}
                className="flex-1 px-3 py-1.5 border border-gray-300 text-sm focus:ring-2 focus:ring-blue-300"
              style={{ borderColor: unreadAanvragen.size > 0 ? '#ef4444' : undefined }}
              >
                <option value="">{t('algemeen')}</option>
                {aanvragen.map(a => (
                  <option key={a.AanvraagID} value={a.AanvraagID}
                    style={{ 
                      background: unreadAanvragen.has(a.AanvraagID) ? '#fef2f2' : undefined,
                      color: unreadAanvragen.has(a.AanvraagID) ? '#dc2626' : undefined,
                      fontWeight: unreadAanvragen.has(a.AanvraagID) ? 700 : undefined
                    }}>
                    {unreadAanvragen.has(a.AanvraagID) ? '🔴 ' : ''}{a.AanvraagID} — {a.LaadPlaats} → {a.LosPlaats}
                    {a.Status ? ` (${a.Status})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="px-4 sm:px-6 lg:px-8 pt-3 flex gap-2 flex-wrap items-center">
              {EMOJIS.map(emoji => (
                <button key={emoji} type="button"
                  onClick={() => setNewMessage(prev => prev + emoji)}
                  className="text-xl hover:bg-gray-100 rounded p-1 transition-colors"
                  title={emoji}
                >{emoji}</button>
              ))}
              <label className="cursor-pointer text-xl hover:bg-gray-100 rounded p-1 transition-colors" title="Foto toevoegen">
                <input type="file" accept="image/*" className="hidden" onChange={handleFotoUpload} />
                📷
              </label>
            </div>
            {fotoPreview && (
              <div className="px-4 sm:px-6 lg:px-8 pt-2 flex items-center gap-2">
                <img src={fotoPreview} alt="preview" className="h-12 rounded border border-gray-200" />
                <button onClick={() => { setFotoPreview(null); setFotoData(null); }}
                  className="bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">✕</button>
              </div>
            )}
            <div className="px-4 sm:px-6 lg:px-8 py-3 flex gap-3">
              <textarea
                ref={inputRef}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKey}
                placeholder={t('typ_bericht')}
                disabled={sending}
                rows={2}
                className="flex-1 px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-blue-300 focus:border-transparent disabled:bg-gray-100 resize-none text-sm"
              />
              <button
                onClick={handleSend}
                disabled={(!newMessage.trim() && !fotoData) || sending}
                className={`px-6 py-3 font-semibold transition-all flex items-center gap-2 ${
                  (!newMessage.trim() && !fotoData) || sending
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-900 to-blue-800 text-white hover:from-blue-800 hover:to-blue-700 shadow-lg'
                }`}
              >
                <Send className="w-5 h-5" />
                <span className="hidden sm:inline">{sending ? '...' : t('verstuur')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerMessages;
